/**
 * User Management Page Container
 * Main page for admin to manage users
 *
 * Features:
 * - List all users with pagination and filtering
 * - Create new user (local or remote flow)
 * - Edit user details
 * - Delete user (soft-delete)
 * - Reset password
 * - View user details
 *
 * @module Features/UserManagement/Pages
 */

import React, { useEffect } from 'react';
import { Button, Card, Container, LinearProgress, Stack, Typography, Alert } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../App/store';
import {
  openCreateDialog,
  setPage,
  setPageSize,
  setSearchEmail,
  setRoleFilter,
  setIsActiveFilter,
  setEmailConfirmedFilter,
  clearFilters,
  openUpdateDialog,
  openDeleteDialog,
  openResetPasswordDialog,
  openDetailsDialog,
} from '../slices/userManagementSlice';
import { useListUsersQuery } from '../Service/usersApi';
import { UserListTable } from '../Components/UserListTable';
import { UserFiltersPanel } from '../Components/UserFiltersPanel';
import CreateUserDialog from '../Dialogs/CreateUserDialog';
import UpdateUserDialog from '../Dialogs/UpdateUserDialog';
import DeleteUserDialog from '../Dialogs/DeleteUserDialog';
import ResetPasswordDialog from '../Dialogs/ResetPasswordDialog';
import UserDetailsDialog from '../Dialogs/UserDetailsDialog';

/**
 * User Management Page Container Component
 */
const UserManagementPage: React.FC = () => {
  const dispatch = useDispatch();

  // Redux state
  const { filters, pagination, dialogs, selectedUserId } = useSelector(
    (state: RootState) => state.userManagement
  );
  const token = useSelector((state: RootState) => state.auth.token);

  // RTK Query (skip if no token to prevent ConditionError)
  const {
    data: usersData,
    isLoading,
    error,
  } = useListUsersQuery(
    {
      page: pagination.page,
      pageSize: pagination.pageSize,
      searchEmail: filters.searchEmail,
      role: filters.role,
      isActive: filters.isActive,
      emailConfirmed: filters.emailConfirmed,
    },
    {
      skip: !token, // Only fetch when authenticated with valid token
    }
  );

  // Log mounted and auth state
  useEffect(() => {
    console.log('UserManagementPage mounted');
    console.log('Token available:', !!token);
    console.log('Token value:', token ? `${token.substring(0, 20)}...` : 'null');
  }, [token]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <div>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Zarządzanie użytkownikami
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Twórz, edytuj i zarządzaj kontami użytkowników
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => dispatch(openCreateDialog())}
          size="large"
        >
          Utwórz użytkownika
        </Button>
      </Stack>

      {/* Loading bar */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* No token warning */}
      {!token && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Brak tokenu uwierzytelnienia. Zaloguj się ponownie.
        </Alert>
      )}

      {/* Filters */}
      <UserFiltersPanel
        filters={filters}
        onSearchEmailChange={(email) => dispatch(setSearchEmail(email))}
        onRoleChange={(role) => dispatch(setRoleFilter(role))}
        onIsActiveChange={(value) => dispatch(setIsActiveFilter(value))}
        onEmailConfirmedChange={(value) => dispatch(setEmailConfirmedFilter(value))}
        onClearFilters={() => dispatch(clearFilters())}
      />

      {/* Users Table */}
      <Card>
        <UserListTable
          data={usersData?.items}
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalCount={usersData?.totalCount || 0}
          isLoading={isLoading}
          error={error}
          onPageChange={(newPage) => dispatch(setPage(newPage))}
          onPageSizeChange={(newPageSize) => dispatch(setPageSize(newPageSize))}
          onEdit={(userId) => dispatch(openUpdateDialog(userId))}
          onDelete={(userId) => dispatch(openDeleteDialog(userId))}
          onResetPassword={(userId) => dispatch(openResetPasswordDialog(userId))}
          onDetails={(userId) => dispatch(openDetailsDialog(userId))}
        />
      </Card>

      {/* Dialogs */}
      {dialogs.createOpen && <CreateUserDialog />}
      {dialogs.updateOpen && selectedUserId && <UpdateUserDialog userId={selectedUserId} />}
      {dialogs.deleteOpen && selectedUserId && <DeleteUserDialog userId={selectedUserId} />}
      {dialogs.resetPasswordOpen && selectedUserId && (
        <ResetPasswordDialog userId={selectedUserId} />
      )}
      {dialogs.detailsOpen && selectedUserId && <UserDetailsDialog userId={selectedUserId} />}
    </Container>
  );
};

export default UserManagementPage;
